import { AuthFetchWithResponse } from 'src/Types/api'
import { getStorageTypeFromPairingId } from '../../parsers'
import { DeviceStatus, LastKnownDeviceStatus, PairingState } from './installations'
import { ApiResult, throwApiError } from 'src/Libs/api'
import { StatusCodes } from '@e3dc-react/shell/Libs'
import { AuthFetchError } from '@e3dc-react/shell/Types'

export interface DeviceLinkDto {
  createdAt: string
  deletedAt: string
  deviceId: string
  deviceShortUuid: string
  deviceStatus: DeviceStatus
  /**
   * Bits from embedded representing the devices errors and/or warning.
   * 0 is no error/warnings.
   */
  deviceStatusCode: number | null
  deviceType: 'emc' | 'storage'
  deviceName: string
  deviceUuid: string
  id: string | number
  installationId: string
  isMigrated: boolean
  lastKnownDeviceStatus: LastKnownDeviceStatus
  lastKnownDeviceStatusTimestamp: string
  nextDeviceLinkId: string
  pairingState: PairingState
  prevDeviceLinkId: string
  /** ISO date */
  startAt: string
  /** ISO date, null is the device has not stopped yet. */
  stopAt: string
  /** ISO date */
  updatedAt: string

  /** Unused */
  productName: string
}

export const getDeviceLinks = async (
  installationId: number,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<ArrayType<Nullable<DeviceLinkDto>> | null>> => {
  try {
    const response = await authFetch<ArrayType<Nullable<DeviceLinkDto>>>({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/device-links`,
    })

    if (response.status !== StatusCodes.OK) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<ArrayType<Nullable<DeviceLinkDto>>>(response.data)
  } catch (error) {
    return throwApiError(error, 'Failed to get device links:')
  }
}

type DeviceLinkPOSTorPUTDto = Pick<DeviceLinkDto, 'deviceShortUuid' | 'deviceType'> & {
  /**
   * - true for "emc"
   * - false for "storage"
   */
  isEmsMaster: boolean
  /** Used only for "storage" POST request. */
  deviceName?: string
}
const createDeviceLink = async (
  installationId: number,
  deviceLink: DeviceLinkPOSTorPUTDto,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<number | null>> => {
  try {
    const response = await authFetch<number>({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/device-links`,
      method: 'POST',
      payload: deviceLink,
    })

    if (response.status !== StatusCodes.OK && response.status !== StatusCodes.CREATED) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<number>(response.data)
  } catch (error) {
    return throwApiError(error, 'Failed to create device link:')
  }
}

const updateDeviceLink = async (
  installationId: number,
  deviceLinkId: string,
  deviceLink: DeviceLinkPOSTorPUTDto | { deviceId: string },
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<number | null>> => {
  try {
    const response = await authFetch<number>({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/device-links/${deviceLinkId}`,
      method: 'PUT',
      payload: deviceLink,
    })

    if (response.status !== StatusCodes.OK) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<number>(response.data)
  } catch (error) {
    return throwApiError(error, 'Failed to update device link:')
  }
}

const DEVICE_ID_REGEX = new RegExp('^[0-9]+$')
export const updateDeviceLinkPairingId = async (
  installationId: number,
  deviceLinkId: string,
  oldPairingId: string,
  newPairingId: string,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<ArrayType<Nullable<DeviceLinkDto>> | null>> => {
  const id = newPairingId.replace('XEM-', '').trim()

  const payload = DEVICE_ID_REGEX.test(id)
    ? {
        deviceId: id,
      }
    : {
        deviceShortUuid: id,
      }

  try {
    const response = await authFetch<ArrayType<Nullable<DeviceLinkDto>>>({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/device-links/${deviceLinkId}${oldPairingId ? '/replacement' : ''}`,
      method: oldPairingId ? 'POST' : 'PUT',
      payload,
    })

    if (response.status !== StatusCodes.OK) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<ArrayType<Nullable<DeviceLinkDto>>>(response.data)
  } catch (error) {
    return throwApiError(error, 'Failed to update device link pairing ID:')
  }
}

export const createEmcDeviceLink = (
  installationId: number,
  pairingId: string,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<number | null>> => {
  return createDeviceLink(
    installationId,
    {
      deviceShortUuid: pairingId,
      deviceType: 'emc',
      isEmsMaster: true,
    },
    authFetch
  )
}

/** Kept for reference: */
// export const updateEmcDeviceLink = (
//   installationId: number,
//   deviceLinkId: string,
//   pairingId: string,
//   authFetch: AuthFetchWithResponse
// ): Promise<ApiResult<number | null>> => {
//   return updateDeviceLink(
//     installationId,
//     deviceLinkId,
//     {
//       deviceShortUuid: pairingId,
//       deviceType: 'emc',
//       isEmsMaster: true,
//     },
//     authFetch
//   )
// }

export const createStorageDeviceLink = (
  installationId: number,
  name: string,
  pairingId: string,
  authFetch: AuthFetchWithResponse
): Promise<number | null> => {
  const storageType = getStorageTypeFromPairingId(pairingId)

  if (storageType === 'XEM - R1') {
    let newStorageId: number
    return createDeviceLink(
      installationId,
      {
        deviceName: name,
        deviceShortUuid: '',
        deviceType: 'storage',
        isEmsMaster: false,
      },
      authFetch
    )
      .then(res => res.value)
      .then(storageId => {
        if (storageId === null) {
          throw new Error('Failed to create storage device link')
        }
        newStorageId = storageId
        return updateDeviceLink(installationId, storageId.toString(), { deviceId: pairingId }, authFetch)
      })
      .then(() => newStorageId)
  }

  return createDeviceLink(
    installationId,
    {
      deviceName: name,
      deviceShortUuid: pairingId,
      deviceType: 'storage',
      isEmsMaster: false,
    },
    authFetch
  ).then(res => res.value)
}

/* Kept for reference: */
// export const updateStorageDeviceLink = (
//   installationId: number,
//   deviceLinkId: string,
//   pairingId: string,
//   authFetch: AuthFetchWithResponse
// ): Promise<ApiResult<number | null>> => {
//   const storageType = getStorageTypeFromPairingId(pairingId)
//   if (storageType === 'XEM - R1') {
//     return updateDeviceLink(installationId, deviceLinkId, { deviceId: pairingId }, authFetch)
//   }

//   return updateDeviceLink(
//     installationId,
//     deviceLinkId,
//     {
//       deviceShortUuid: pairingId,
//       deviceType: 'storage',
//       isEmsMaster: false,
//     },
//     authFetch
//   )
// }

export const deleteDeviceLink = async (installationId: number, deviceLinkId: string, authFetch: AuthFetchWithResponse): Promise<ApiResult<null>> => {
  try {
    const response = await authFetch({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/device-links/${deviceLinkId}`,
      method: 'DELETE',
    })

    if (response.status !== StatusCodes.OK && response.status !== StatusCodes.NO_CONTENT) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<null>(null)
  } catch (error) {
    return throwApiError(error, 'Failed to delete device link:')
  }
}
